import { Button } from "../../components/Button";
import Input from "../../components/Input";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";

import { userState, selectPasswordRequirements } from "./userSlice";
import { Oval } from "react-loader-spinner";
import { showNotification } from "../../components/Notification";
import { formatLabel } from "../../uitls/Utils";
import { resetPassword, getPasswordRequirements } from "../../api/userApi";
import { Card } from "@material-tailwind/react";
import { Tooltip } from "@mui/material";
import {
  faCheck,
  faTimes,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PasswordRequirements from "../../components/PasswordRequirements";

const UpdatePassword = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // Get email from location state or localStorage
  const email =
    location.state?.email ||
    JSON.parse(localStorage.getItem("user") || "{}")?.corporateEmail;

  const [formValues, setFormValues] = useState({ corporateEmail: email });
  const [formErrors, setFormErrors] = useState({});

  const [pwd, setPwd] = useState("");
  const [matchPwd, setMatchPwd] = useState("");
  const [validPwd, setValidPwd] = useState(true);

  const [validMatch, setValidMatch] = useState(false);
  const [submitReferenceState, setSubmitReferenceState] = useState(false);

  const stateOfUser = useSelector(userState);
  const passwordRequirements = useSelector(selectPasswordRequirements);

  // Fetch password requirements on component mount
  useEffect(() => {
    if (passwordRequirements.length === 0) {
      dispatch(getPasswordRequirements());
    }
  }, [dispatch, passwordRequirements.length]);

  // Format requirements for tooltip
  const requirementsText = passwordRequirements.length > 0 
    ? passwordRequirements.join(". ") 
    : "Must include uppercase and lowercase letters, a number and a special character. Allowed special characters: !@#$%*";

  useEffect(() => {
    if (
      !stateOfUser.error &&
      submitReferenceState &&
      stateOfUser.status === "succeeded"
    ) {
      showNotification("success", "Password Reset Successfully...");
      navigate("/signIn");
    }
    setSubmitReferenceState(false);
  }, [submitReferenceState]);

  const requiredInputFields = {
    otp: true,
    password: true,
  };

  const PWD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!*@#$%]).{8,24}$/;
  useEffect(() => {
    const result = PWD_REGEX.test(matchPwd);
    if (result && validMatch) {
      setFormValues((prevValues) => ({ ...prevValues, password: matchPwd }));
      setFormErrors((prevValues) => ({ ...prevValues, password: "" }));
    } else {
      setFormValues((prevValues) => ({ ...prevValues, password: undefined }));
    }
  }, [pwd, matchPwd, validMatch]);

  useEffect(() => {
    const result = PWD_REGEX.test(pwd);
    setValidPwd(result);
    const match = pwd === matchPwd;
    setValidMatch(match);
  }, [pwd, matchPwd]);

  const trimObjectValues = (obj) => {
    const newObj = { ...obj, isTempPasswordChanged: true };
    if (Array.isArray(newObj)) {
      return newObj.map(trimObjectValues);
    } else if (typeof newObj === "object" && newObj !== null) {
      return Object.keys(newObj).reduce((acc, key) => {
        acc[key] = trimObjectValues(newObj[key]);
        return acc;
      }, {});
    } else if (typeof newObj === "string") {
      return newObj.trim();
    }
    return newObj;
  };

  // Helper function to validate email format
  const validateEmail = (value, key) => {
    const emailFields = ["email", "personalEmail", "corporateEmail"];
    if (emailFields.includes(key) && value) {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailPattern.test(value) ? "" : "Invalid email format";
    }
    return "";
  };

  // Helper function to validate password
  const validatePassword = (value, key) => {
    if (key !== "password") return "";

    if (!value) {
      return !validPwd ? "Enter a valid Password" : "";
    }

    if (!validPwd) {
      if (value.length < 8) {
        return "Password must have atleast 8 characters!";
      }
      return "Enter a valid Password";
    }

    return "";
  };

  // Helper function to validate phone number
  const validatePhoneNumber = (value, key) => {
    const phoneFields = ["phoneNumber", "contactNumber"];
    if (phoneFields.includes(key) && value) {
      return value.length === 10 ? "" : "Phone Number must have 10 Digits!";
    }
    return "";
  };

  // Helper function to validate string fields
  const validateStringField = (value, key) => {
    const trimmedValue = value.trim();

    if (!trimmedValue) {
      return `${formatLabel(key)} is required`;
    }

    // Check email validation
    const emailError = validateEmail(trimmedValue, key);
    if (emailError) return emailError;

    // Check password validation
    const passwordError = validatePassword(trimmedValue, key);
    if (passwordError) return passwordError;

    // Check phone number validation
    const phoneError = validatePhoneNumber(trimmedValue, key);
    if (phoneError) return phoneError;

    return "";
  };

  // Helper function to validate non-string fields
  const validateNonStringField = (value, key) => {
    if (value === null || value === undefined || value === "") {
      return `${formatLabel(key)} is required`;
    }
    return "";
  };

  // Helper function to validate a single field
  const validateField = (key) => {
    const value = formValues[key];

    if (!requiredInputFields?.[key]) {
      return "";
    }

    if (typeof value === "string") {
      return validateStringField(value, key);
    }

    return validateNonStringField(value, key);
  };

  const validateForm = () => {
    const validationErrors = {};

    Object.keys(requiredInputFields)?.forEach((key) => {
      const error = validateField(key);
      if (error) {
        validationErrors[key] = error;
      }
    });

    setFormErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      dispatch(resetPassword(trimObjectValues(formValues)))
        .unwrap()
        .then(() => {
          setSubmitReferenceState(true);
        })
        .catch((error) => {
          console.error("Error:", error);
          showNotification(
            "error",
            error?.message || "Something went wrong..."
          );
        });
    }
  };

  const handleChange = (name) => (e) => {
    setFormValues((prevValues) => ({ ...prevValues, [name]: e.target.value }));

    // Extract error message logic
    let errorMessage = "";
    if (requiredInputFields[name]) {
      if (e.target.value.trim()) {
        errorMessage = "";
      } else {
        errorMessage = `${formatLabel(name)} is required`;
      }
    } else {
      errorMessage = "";
    }

    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [name]: errorMessage,
    }));
  };

  return (
    <div className="min-h-screen bg-white xsm:bg-[#055173]  ">
      <div className="absolute inset-0 flex justify-center items-center sm:p-5 ">
        <div className=" w-full p-2 xsm:p-0  grid grid-cols-1 md:grid-cols-2   sm:w-[90%] lg:w-[90%] h-[90%] ">
          <div className="hidden md:flex items-center justify-center bg-white rounded-l-lg">
            <div className=" flex h-[80%] justify-center self-center items-center">
              <img
                src="/images/resetPassword.png"
                alt="Password reset illustration"
                style={{
                  width: "70%",
                  height: "70%",
                  borderRadius: "10%",
                  objectFit: "fill",
                }}
              />
            </div>
          </div>
          <Card className="bg-white xsm:p-5 flex flex-col items-center rounded-l-none">
            <div className="mb-8 pt-8 w-full">
              <div className="w-full flex flex-col mb-1 justify-start">
                <h2 className="flex text-xl font-semibold sm:text-xl flex-1 mb-4 w-full">
                  Password Update Required
                </h2>
                <p className="text-gray-600 text-sm mb-8 w-full">
                  Enter verification code and proceed with changing your
                  password.
                </p>
              </div>
            </div>
            <div className="flex flex-col justify-start w-full ml-2 "></div>
            <div className="w-full">
              <div className="mb-5 w-full">
                <Input
                  autoFocus={true}
                  label="Verification Code"
                  name="otp"
                  type="text"
                  placeholder="Enter verification Code"
                  requiredField={requiredInputFields?.otp}
                  value={formValues?.otp}
                  onChange={handleChange("otp")}
                  error={formErrors?.otp && "Verification Code is required"}
                />
              </div>
              <div className="w-full mb-4">
                <div className="">
                  <p className="">
                    Password <span className="text-red-600">*</span>
                    <Tooltip
                      placement="top"
                      title={requirementsText}
                    >
                      <span className="ml-1">
                        <FontAwesomeIcon
                          icon={faInfoCircle}
                          className="text-blue-gray-700"
                        />
                      </span>
                    </Tooltip>
                    <Tooltip placement="top" title={"Valid Password"}>
                      <span
                        className={validPwd ? "text-[#32CD32] ml-1" : "hidden"}
                      >
                        <FontAwesomeIcon icon={faCheck} />
                      </span>
                    </Tooltip>
                    <Tooltip placement="top" title="Invalid Password">
                      <span
                        className={
                          validPwd || !pwd ? "hidden" : "text-red-600 ml-1"
                        }
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </span>
                    </Tooltip>
                  </p>
                </div>
                <div className="w-full">
                  <Input
                    className="h-[39px]"
                    placeholder="Password"
                    type="password"
                    value={pwd}
                    onChange={(e) => setPwd(e.target.value)}
                    error={formErrors?.password}
                    requiredField={requiredInputFields?.password}
                  />
                </div>
                {/* Show password requirements when user starts typing */}
                {pwd && (
                  <div className="mt-2">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 md:p-4">
                      <p className="text-sm font-semibold text-blue-900 mb-2">
                        Password Requirements:
                      </p>
                      <PasswordRequirements 
                        className="text-blue-800" 
                        showTitle={false}
                      />
                    </div>
                  </div>
                )}
              </div>
              <div className="w-full mb-4">
                <div className="">
                  <p className="">
                    Confirm Password <span className="text-red-600">*</span>
                    <Tooltip
                      placement="top"
                      title={"  Must match the New password."}
                    >
                      <span className="ml-1">
                        <FontAwesomeIcon
                          icon={faInfoCircle}
                          className="text-blue-gray-700"
                        />
                      </span>
                    </Tooltip>
                    <Tooltip placement="top" title="Valid Match">
                      <span
                        className={
                          validMatch && matchPwd && validPwd
                            ? "text-[#32CD32] ml-1"
                            : "hidden"
                        }
                      >
                        <FontAwesomeIcon icon={faCheck} />
                      </span>
                    </Tooltip>
                    <Tooltip placement="top" title="Invalid Match">
                      <span
                        className={
                          (validMatch && validPwd) || !matchPwd
                            ? "hidden"
                            : "text-red-600 ml-1"
                        }
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </span>
                    </Tooltip>
                  </p>
                </div>
                <div className="w-full">
                  <Input
                    className="h-[39px] "
                    placeholder="Confirm Password"
                    type="password"
                    value={matchPwd}
                    onChange={(e) => setMatchPwd(e.target.value)}
                    error={formErrors?.password}
                    requiredField={requiredInputFields?.password}
                  />
                </div>
              </div>

              <div className="flex flex-col items-center justify-center w-full mb-2 h-8">
                {stateOfUser?.loading && (
                  <Oval
                    height={35}
                    width={35}
                    color={"#1f7fbb"}
                    ariaLabel="loading"
                    strokeWidth={3}
                  />
                )}
              </div>
              <div className="flex flex-col justify-center items-center w-full">
                <Button
                  className="w-full"
                  type="button"
                  value="Reset"
                  disabled={!validMatch || !validPwd || stateOfUser?.loading}
                  handleClick={handleSubmit}
                />
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default UpdatePassword;
